import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Headline3, Label2, ButtonRound, BUTTON_STYLE, Label1, Loader } from "@bms-ai/bms-ai-ui-lib/components";
import { createTaskNoTokenAPI } from "../../api/data-services/task-api";
import { getChecksAPI, type Check, type GetChecksPayload } from "../../api/data-services/checks-api";
import { useQATask } from "../../context/QATaskContext";
import { useCustomToast } from "../common/toast";
import "./SelectChecksModal.scss";

type SelectChecksModalProps = {
  open: boolean;
  onClose: () => void;
  selectedBrand?: any;
  selectedChannel?: any;
  uploadedFiles?: any;
  roleSelection?: string | null;
  figmaDetails?: any;
  uploadRequirements?: any;
  uploadResponses?: any;
  animatedBannerFrames?: any[] | null;
  staticBannerFrames?: any[] | null;
  fteStoryboardType?: string | null;
  sessionDetails?: any;
  roleName?: string;
  moduleAccessToken?: string;
};

export default function SelectChecksModal({
  open,
  onClose,
  selectedBrand,
  selectedChannel,
  uploadedFiles,
  roleSelection,
  figmaDetails,
  // uploadRequirements,
  uploadResponses,
  animatedBannerFrames,
  staticBannerFrames,
  fteStoryboardType,
  sessionDetails,
  roleName,
  moduleAccessToken,
}: SelectChecksModalProps) {
  const [checksData, setChecksData] = useState<Check[]>([]);
  const [selectedChecks, setSelectedChecks] = useState<Set<string>>(new Set());
  const [creatingTask, setCreatingTask] = useState(false);
  const [loadingChecks, setLoadingChecks] = useState(false);
  const [disabledChecks, setDisabledChecks] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const { setTaskId, setError, setTaskCreated } = useQATask();
  const { showToast } = useCustomToast();

  // Fetch checks when modal opens
  useEffect(() => {
    if (!open || !selectedBrand || !selectedChannel) return;

    const fetchChecks = async () => {
      try {
        setLoadingChecks(true);
        
        const payload: GetChecksPayload = {
          channel_id: selectedChannel.value || "",
          brand_id: selectedBrand.brandId || "",
          storyboard: !!uploadedFiles?.storyboard,
          manuscript: !!uploadedFiles?.manuscript,
          figma: !!figmaDetails?.url,
          brief: !!uploadedFiles?.brief,
          role: roleSelection || undefined,
          fte_storyboard_type: fteStoryboardType || undefined,
        };

        console.log("Fetching checks with payload:", payload, "and fteStoryboardType:", fteStoryboardType, "and moduleAccessToken:", moduleAccessToken);
        const response = await getChecksAPI(payload, moduleAccessToken);
        
        if (response?.success && Array.isArray(response?.data) && response.data.length > 0) {
          setChecksData(response.data);
          
          // Initialize all checks as selected by default
          let selectedCheckIds = new Set(response.data.map((c) => c.check_id));
          const newDisabledChecks = new Set<string>();
          
          // If animated banner, check if any dimension has >6 frames and disable "Animation Notes Match"
          const isAnimatedBanner = selectedChannel?.label === "Animated Banner";
          if (isAnimatedBanner && animatedBannerFrames && Array.isArray(animatedBannerFrames)) {
            const framesByDimension = new Map<string, number>();
            animatedBannerFrames.forEach((frame) => {
              const dim = frame.dimension || "Unknown";
              framesByDimension.set(dim, (framesByDimension.get(dim) || 0) + 1);
            });
            
            const hasLargerDimension = Array.from(framesByDimension.values()).some((count) => count > 6);
            
            if (hasLargerDimension) {
              const animationNotesCheck = response.data.find(
                (c) => c.check_name?.toLowerCase().includes("animation notes match"),
              );
              if (animationNotesCheck) {
                showToast("Animation Notes Match check has been disabled because one or more dimensions have more than 6 frames.", 5000);
                newDisabledChecks.add(animationNotesCheck.check_id);
                selectedCheckIds.delete(animationNotesCheck.check_id);
              }
            }
          }
          
          setSelectedChecks(selectedCheckIds);
          setDisabledChecks(newDisabledChecks);
        } else {
          console.warn("No checks available for the selected criteria");
          setChecksData([]);
          setSelectedChecks(new Set());
        }
      } catch (error) {
        console.error("Failed to load checks:", error);
        // Keep empty state if API fails
        setChecksData([]);
        setSelectedChecks(new Set());
      } finally {
        setLoadingChecks(false);
      }
    };

    fetchChecks();
  }, [open, selectedBrand?.brandId, selectedChannel?.value, roleSelection, fteStoryboardType, moduleAccessToken]);

  const categories = useMemo(() => {
    const cats = [...new Set(checksData.map((c) => c.category_name))];
    return cats;
  }, [checksData]);

  const checksByCategory = useMemo(() => {
    const grouped: Record<string, Check[]> = {};
    categories.forEach((cat) => {
      grouped[cat] = checksData.filter((c) => c.category_name === cat);
    });
    return grouped;
  }, [categories, checksData]);

  const isAllSelected = selectedChecks.size === checksData.length;
  const isCategorySelected = (category: string): boolean => {
    const categoryChecks = checksByCategory[category];
    return categoryChecks.every((check) => selectedChecks.has(check.check_id));
  };

  const isCategoryPartialSelected = (category: string): boolean => {
    const categoryChecks = checksByCategory[category];
    const selectedInCategory = categoryChecks.filter((check) =>
      selectedChecks.has(check.check_id),
    );
    return selectedInCategory.length > 0 && selectedInCategory.length < categoryChecks.length;
  };

  const isCategoryDisabled = (category: string): boolean => {
    const categoryChecks = checksByCategory[category];
    return categoryChecks.every((check) => disabledChecks.has(check.check_id));
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedChecks(new Set());
    } else {
      setSelectedChecks(new Set(checksData.map((c) => c.check_id).filter((checkId) => !disabledChecks.has(checkId))));
    }
  };

  const handleSelectCategory = (category: string) => {
    // Prevent selecting disabled categories
    if (isCategoryDisabled(category)) return;
    
    const categoryChecks = checksByCategory[category];
    const isSelected = isCategorySelected(category);

    const newSelected = new Set(selectedChecks);
    if (isSelected) {
      categoryChecks.forEach((check) => newSelected.delete(check.check_id));
    } else {
      categoryChecks.forEach((check) => {
        if (!disabledChecks.has(check.check_id)) {
          newSelected.add(check.check_id);
        }
      });
    }
    setSelectedChecks(newSelected);
  };

  const handleSelectCheck = (checkId: string) => {
    // Prevent selecting disabled checks
    if (disabledChecks.has(checkId)) return;
    
    const newSelected = new Set(selectedChecks);
    if (newSelected.has(checkId)) {
      newSelected.delete(checkId);
    } else {
      newSelected.add(checkId);
    }
    setSelectedChecks(newSelected);
  };

  const handleConfirm = async () => {
    console.log("Selected checks:", Array.from(selectedChecks));

    const fteLabel = sessionDetails?.region === "jp" ? "rte" : "fte";
    const brandId = selectedBrand?.brandId || "";
    const channelId = selectedChannel?.value || "";
    const isFteChannel = selectedChannel?.label
      ?.toLowerCase()
      .includes(fteLabel);

    // Create payload for QA processing
    const payload: any = {
      storyboard_file_id: uploadResponses?.storyboard?.DocID || "",
      storyboard_file_name: uploadResponses?.storyboard?.filename || "",
      manuscript_file_id: uploadResponses?.manuscript?.DocID || "",
      manuscript_file_name: uploadResponses?.manuscript?.filename || "",
      brief_file_id: uploadResponses?.brief?.DocID || "",
      brief_file_name: uploadResponses?.brief?.filename || "",
      figma_url: figmaDetails?.url || "",
      figma_page_name: figmaDetails?.frameName || "",
      brand_id: brandId,
      channel_id: channelId,
      client_id: sessionDetails?.client_id || "",
      user_id: sessionDetails?.user_id || "",
      enterpriseid: sessionDetails?.enterpriseid || "",
      checks_list: Array.from(selectedChecks).filter((checkId) => !disabledChecks.has(checkId)),
    };

    // Add role if Operations, Admin, or Brief Admin user
    const allowedRoles = ["Operations", "Admin", "Brief Admin"];
    if (allowedRoles.includes(roleName || "")) {
      payload.role = roleSelection || "qaoperator";
    }

    console.log("Payload for QA task creation:", isFteChannel, uploadedFiles?.storyboard, fteStoryboardType, payload);
    // Add FTE storyboard type if channel is FTE and storyboard is uploaded
    if (isFteChannel && uploadedFiles?.storyboard && fteStoryboardType) {
      payload.fte_storyboard_type = fteStoryboardType.toLowerCase();
    }

    // For animated banner storyboard, add frames to payload
    const isAnimatedBanner = selectedChannel?.label === "Animated Banner";
    if (isAnimatedBanner && animatedBannerFrames && Array.isArray(animatedBannerFrames)) {
      payload.frames = animatedBannerFrames;
    }

    // For static banner storyboard, add frames to payload
    const isStaticBanner = selectedChannel?.label === "Static Banner";
    if (isStaticBanner && staticBannerFrames && Array.isArray(staticBannerFrames)) {
      payload.frames = staticBannerFrames;
    }

    // Create task and get task_id
    setCreatingTask(true);
    try {
      const response = await createTaskNoTokenAPI(payload, moduleAccessToken);

      if (response && response.task_id) {
        setTaskId(response.task_id);
        setTaskCreated(response.task_id);
        onClose();
        navigate("/qa-results");
      } else {
        throw new Error("Invalid response format - missing task_id");
      }
    } catch (error) {
      console.error("Failed to create task:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create QA task";
      setError(errorMessage);
      alert(`Failed to create QA task: ${errorMessage}`);
    } finally {
      setCreatingTask(false);
    }
  };

  if (!open) return null;

  return (
    <div className="select-checks-modal-overlay">
      <div className="select-checks-modal">
        <div className="modal-header">
          <Headline3 className="modal-title">Select Your Checks</Headline3>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-content">
          {loadingChecks ? (
            <div className="checks-loading">
              <Loader style="inline" />
              {/* <Label2>Loading checks...</Label2> */}
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="select-all-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">
                    <Label1 strong>Select All</Label1>
                  </span>
                </label>
              </div>

              {/* Categories and Checks */}
              <div className="checks-container">
                {categories.map((category) => {
                  const isCatDisabled = isCategoryDisabled(category);
                  return (
                    <div key={category} className="category-section">
                      <div className="category-header">
                        <label
                          className={`checkbox-label ${isCatDisabled ? "disabled" : ""}`}
                          style={{ opacity: isCatDisabled ? 0.5 : 1, pointerEvents: isCatDisabled ? "none" : "auto" }}
                        >
                          <input
                            type="checkbox"
                            checked={isCategorySelected(category)}
                            ref={(el) => {
                              if (el) {
                                el.indeterminate = isCategoryPartialSelected(category);
                              }
                            }}
                            onChange={() => handleSelectCategory(category)}
                            disabled={isCatDisabled}
                            className="checkbox-input"
                          />
                          <span className="checkbox-text">
                            <Label1 strong>{category}</Label1>
                          </span>
                        </label>
                      </div>

                      <div className="checks-list">
                        {checksByCategory[category].map((check) => {
                          const isCheckDisabled = disabledChecks.has(check.check_id);
                          return (
                            <label
                              key={check.check_id}
                              className={`checkbox-label check-item ${isCheckDisabled ? "disabled" : ""}`}
                            style={{ opacity: isCheckDisabled ? 0.5 : 1, pointerEvents: isCheckDisabled ? "none" : "auto" }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedChecks.has(check.check_id)}
                              onChange={() => handleSelectCheck(check.check_id)}
                              disabled={isCheckDisabled}
                              className="checkbox-input"
                            />
                            <span className="checkbox-text">
                              <Label2>
                                {check.check_name}
                                {isCheckDisabled && <span style={{ marginLeft: "8px", color: "#999", fontSize: "0.9em" }}>(Disabled)</span>}
                              </Label2>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <ButtonRound
            className="cancel-btn"
            children="Cancel"
            onClick={onClose}
            style={BUTTON_STYLE.outline}
          />
          <ButtonRound
            className="confirm-btn"
            children={creatingTask ? "Creating Task..." : "Run analysis"}
            onClick={handleConfirm}
            disabled={selectedChecks.size === 0 || creatingTask}
          />
        </div>
      </div>
    </div>
  );
}
